import ABSScrollbars from '@abs/core/ABSScrollbars';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ComputerIcon from '@mui/icons-material/Computer';
import withReducer from 'app/store/withReducer';
import { Fragment, memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ABSSvgIcon from '@abs/core/ABSSvgIcon';
import Tooltip from '@mui/material/Tooltip';
import format from 'date-fns/format';
import ABSUtils from '@abs/utils';
import userSettingsReducer from 'app/main/usersettings/store';
import { getNotifPreferences, saveNotifPreferences } from 'app/main/usersettings/store/userSettingsSlice';
import { openDialog, closeDialog } from 'app/store/abs/dialogSlice';
import { showMessage } from 'app/store/abs/messageSlice';
import { useTranslation } from 'react-i18next';
import NotificationCard from './NotificationCard';
import {
  communicateWithAPI,
  selectNotifications,
  selectDismissingAll,
  setDismissingAll,
} from './store/notificationSlice';
import reducer from './store';
import {
  closeNotificationPanel,
  selectNotificationPanelState,
  toggleNotificationPanel,
} from './store/stateSlice';

const PLATFORM_ICON = {
  android: <PhoneAndroidIcon sx={{ fontSize: 18 }} />,
  ios: <PhoneIphoneIcon sx={{ fontSize: 18 }} />,
  web: <ComputerIcon sx={{ fontSize: 18 }} />,
};

function formatDevicePlatformApp(device) {
  const platform = device.Platform ? `${device.Platform.charAt(0).toUpperCase()}${device.Platform.slice(1)}` : 'Web';
  return `${platform} - ${device.App || 'ABS Web'}`;
}

const ALL_GROUPS = [
  { labelKey: 'NOTIF_GROUP_ACTIVITIES', types: ['activity_error'] },
  { labelKey: 'NOTIF_GROUP_REPORTS', types: ['report_finished'] },
  { labelKey: 'NOTIF_GROUP_OBJECTS', types: ['objectitem_assigned', 'objectitem_unassigned'] },
  { labelKey: 'NOTIF_GROUP_REQUESTS', types: ['request_received', 'request_forwarded', 'request_answered'] },
  { labelKey: 'NOTIF_GROUP_TASKS', types: ['task_assigned', 'task_reassigned', 'task_closed', 'task_reopened', 'task_overdue', 'task_now_open'], requiresTasks: true },
];

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.background.default,
    width: 420,
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
}));

function NotificationPanel() {
  const location = useLocation();
  const dispatch = useDispatch();
  const state = useSelector(selectNotificationPanelState);
  const notifications = useSelector(selectNotifications);
  const { t } = useTranslation('abslayouts');
  const { t: tAdmin } = useTranslation('absadministration');
  const dismissingAll = useSelector(selectDismissingAll);

  const [showSettings, setShowSettings] = useState(false);
  const notifPreferences = useSelector(({ userSettings }) => userSettings?.personal?.notifPreferences);
  const tasksEnabled = useSelector(({ user }) => user?.tl?.tasksenabled === 1);
  const hasTLLicence = useSelector(({ user }) => Array.isArray(user?.modules) && user.modules.includes('tl_default'));
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [originalPrefs, setOriginalPrefs] = useState(null);
  const [summaryHour, setSummaryHour] = useState(22);
  const [originalSummaryHour, setOriginalSummaryHour] = useState(22);
  const [saving, setSaving] = useState(false);
  const [pushDevices, setPushDevices] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);

  const groups = ALL_GROUPS.filter(g => !g.requiresTasks || tasksEnabled);

  useEffect(() => {
    if (state) {
      dispatch(closeNotificationPanel());
    }
    // eslint-disable-next-line
  }, [location, dispatch]);

  useEffect(() => {
    if (!state) {
      setShowSettings(false);
      if (originalPrefs != null) {
        setNotifPrefs(JSON.parse(JSON.stringify(originalPrefs)));
        setSummaryHour(originalSummaryHour);
      }
    }
  }, [state]);

  useEffect(() => {
    if (showSettings && hasTLLicence) {
      setLoadingSettings(true);
      dispatch(getNotifPreferences()).then(result => {
        if (result && result !== 'error' && Array.isArray(result.push_devices)) {
          setPushDevices(result.push_devices);
        } else {
          setPushDevices([]);
        }
        setLoadingSettings(false);
      });
    } else if (!showSettings) {
      setPushDevices(null);
    }
  }, [showSettings, hasTLLicence]);

  useEffect(() => {
    if (notifPreferences && notifPreferences.length > 0) {
      const copy = JSON.parse(JSON.stringify(notifPreferences));
      setNotifPrefs(copy);
      setOriginalPrefs(copy);
      const firstWithHour = copy.find(p => p.mail_hour != null);
      const hour = firstWithHour ? Number(firstWithHour.mail_hour) : 22;
      setSummaryHour(hour);
      setOriginalSummaryHour(hour);
    }
  }, [notifPreferences]);

  const handleDeleteDevice = (device_id) => {
    dispatch(openDialog({
      children: (
        <Fragment>
          <DialogTitle>{tAdmin('ATTENTION')}!</DialogTitle>
          <DialogContent>
            <DialogContentText>{tAdmin('NOTIF_PUSH_DEVICE_REMOVE_CONFIRM')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              onClick={async () => {
                dispatch(closeDialog());
                const result = await dispatch(communicateWithAPI('abs_delete_push_device', { device_id }, false));
                if (result && result !== 'error' && Array.isArray(result)) {
                  setPushDevices(result);
                }
              }}
            >
              {tAdmin('YES')}
            </Button>
            <Button color="primary" autoFocus onClick={() => dispatch(closeDialog())}>
              {tAdmin('NO')}
            </Button>
          </DialogActions>
        </Fragment>
      )
    }));
  };

  const handleToggleDevice = async (device_id, currentEnabled) => {
    const newEnabled = currentEnabled === '1' || currentEnabled === 1 ? 0 : 1;
    const result = await dispatch(communicateWithAPI('abs_set_push_device_enabled', { device_id, enabled: newEnabled }, false));
    if (result && result !== 'error' && Array.isArray(result)) {
      setPushDevices(result);
    }
  };

  const handleTestPush = async (device_id) => {
    const result = await dispatch(communicateWithAPI('abs_send_test_push', { device_id }, false));
    if (result && result !== 'error') {
      dispatch(showMessage({ message: tAdmin('NOTIF_PUSH_DEVICE_TEST_SENT'), variant: 'info' }));
    }
  };

  function handleClose() { dispatch(closeNotificationPanel()); }
  function handleDismiss(id) { dispatch(communicateWithAPI('abs_dismiss_notification', { notification_id: id })); }
  function handleDismissAll() {
    const count = notifications.length;
    dispatch(setDismissingAll(true));
    setTimeout(() => {
      dispatch(communicateWithAPI('abs_dismiss_notification', { notification_id: null }));
      dispatch(setDismissingAll(false));
    }, count * 50 + 350);
  }
  function handleMarkAllRead() { dispatch(communicateWithAPI('abs_acknowledge_notification', { notification_id: null })); }
  function handleAcknowledge(id) { dispatch(communicateWithAPI('abs_acknowledge_notification', { notification_id: id })); }

  const handleModeChange = (notifType, channel, value) => {
    setNotifPrefs(prev => prev.map(p =>
      p.notif_type === notifType ? { ...p, [`${channel}_mode`]: value } : p
    ));
  };

  const CHANNELS = ['mail', 'push'];
  const hasChanges = originalPrefs != null && notifPrefs != null && (
    summaryHour !== originalSummaryHour ||
    notifPrefs.some(p => {
      const orig = originalPrefs.find(o => o.notif_type === p.notif_type);
      return !orig || CHANNELS.some(ch => p[`${ch}_mode`] !== orig[`${ch}_mode`]);
    })
  );

  const handleSave = async () => {
    if (!notifPrefs) return;
    setSaving(true);
    const toSave = notifPrefs.map(p => ({ ...p, mail_hour: summaryHour, push_hour: summaryHour }));
    await dispatch(saveNotifPreferences(toSave));
    setSaving(false);
    setShowSettings(false);
  };

  return (
    <StyledSwipeableDrawer
      open={state}
      anchor="right"
      onOpen={() => { }}
      onClose={() => dispatch(toggleNotificationPanel())}
      disableSwipeToOpen
    >
      {showSettings ? (
        <>
          {/* Settings header */}
          <div className="flex items-center justify-between px-16 py-12 border-b">
            <div className="flex items-center gap-8">
              <Tooltip title={t('BACK')} placement="bottom">
                <IconButton size="small" onClick={() => setShowSettings(false)}>
                  <ABSSvgIcon color="action">heroicons-outline:arrow-left</ABSSvgIcon>
                </IconButton>
              </Tooltip>
              <Typography className="text-18 font-semibold leading-none">{t('NOTIFICATION_SETTINGS')}</Typography>
            </div>
            <Tooltip title={t('CLOSE')} placement="bottom">
              <IconButton onClick={handleClose} size="medium">
                <ABSSvgIcon color="action">heroicons-outline:x</ABSSvgIcon>
              </IconButton>
            </Tooltip>
          </div>

          {/* Settings body */}
          <ABSScrollbars className="flex-1">
            {loadingSettings ? (
              <Box className="flex items-center justify-center" sx={{ height: 200 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <div className="px-8 pb-16">
                {hasTLLicence && (
                  <>
                    <Typography variant="h6" className="font-bold px-8 pt-12 pb-4">{tAdmin('TIMELITE')}</Typography>
                    <Divider className="mb-8" />
                    {groups.map(group => (
                      <div key={group.labelKey} className="mb-8">
                        <Box className="flex items-center px-8 py-6" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography className="flex-1 text-12 font-semibold uppercase" color="text.secondary">
                            {tAdmin(group.labelKey)}
                          </Typography>
                          <Typography className="text-11 font-medium" sx={{ width: 84, textAlign: 'center' }} color="text.secondary">
                            {tAdmin('NOTIF_CHANNEL_MAIL')}
                          </Typography>
                          <Typography className="text-11 font-medium" sx={{ width: 84, textAlign: 'center' }} color="text.secondary">
                            {tAdmin('NOTIF_CHANNEL_PUSH')}
                          </Typography>
                        </Box>
                        {group.types.map((notifType) => {
                          const pref = notifPrefs?.find(p => p.notif_type === notifType);
                          return (
                            <Box
                              key={notifType}
                              className="flex flex-wrap items-center gap-4 px-8 py-4"
                              sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <Typography className="flex-1 text-12" sx={{ minWidth: 80 }}>
                                {tAdmin(`NOTIFTYPE_${notifType.toUpperCase()}`)}
                              </Typography>
                              <Select
                                size="small"
                                value={pref?.mail_mode ?? 'off'}
                                onChange={e => handleModeChange(notifType, 'mail', e.target.value)}
                                sx={{ width: 84, fontSize: 11 }}
                                disabled={!pref}
                              >
                                <MenuItem value="instant">{tAdmin('NOTIF_MODE_INSTANT')}</MenuItem>
                                <MenuItem value="daily">{tAdmin('NOTIF_MODE_DAILY')}</MenuItem>
                                <MenuItem value="off">{tAdmin('NOTIF_MODE_OFF')}</MenuItem>
                              </Select>
                              <Select
                                size="small"
                                value={pref?.push_mode ?? 'off'}
                                onChange={e => handleModeChange(notifType, 'push', e.target.value)}
                                sx={{ width: 84, fontSize: 11 }}
                                disabled={!pref}
                              >
                                <MenuItem value="instant">{tAdmin('NOTIF_MODE_INSTANT')}</MenuItem>
                                <MenuItem value="daily">{tAdmin('NOTIF_MODE_DAILY')}</MenuItem>
                                <MenuItem value="off">{tAdmin('NOTIF_MODE_OFF')}</MenuItem>
                              </Select>
                            </Box>
                          );
                        })}
                      </div>
                    ))}

                    <Divider className="my-8" />
                    <div className="flex items-center px-8 py-8 gap-12">
                      <Typography className="flex-1 text-13">{tAdmin('NOTIF_DAILY_SUMMARY_TIME')}</Typography>
                      <Select
                        size="small"
                        value={summaryHour}
                        onChange={e => setSummaryHour(Number(e.target.value))}
                        sx={{ width: 84 }}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00</MenuItem>
                        ))}
                      </Select>
                    </div>

                    <Divider className="my-8" />
                    <Typography variant="h6" className="font-bold px-8 pt-4 pb-2">{tAdmin('NOTIF_PUSH_DEVICES')}</Typography>
                    {pushDevices == null ? null : pushDevices.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" className="px-8 py-4 text-12">
                        {tAdmin('NOTIF_PUSH_DEVICES_EMPTY')}
                      </Typography>
                    ) : (
                      pushDevices.map(device => {
                        const isEnabled = device.Enabled === '1' || device.Enabled === 1;
                        const deviceName = device.Device_Name || device.Platform;
                        return (
                          <Box
                            key={device.Device_ID}
                            className="flex items-center px-8 py-8 gap-8"
                            sx={{ '&:hover': { bgcolor: 'action.hover' }, opacity: isEnabled ? 1 : 0.55 }}
                          >
                            <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              {PLATFORM_ICON[device.Platform] ?? PLATFORM_ICON.web}
                            </Box>
                            <Box className="flex-1 min-w-0">
                              <Typography className="text-12" noWrap>{deviceName}</Typography>
                              {device.Last_Used_at && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {format(ABSUtils.NewDate(device.Last_Used_at), 'dd.MM.yyyy HH:mm')} - {formatDevicePlatformApp(device)}
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title={isEnabled ? tAdmin('NOTIF_PUSH_DEVICE_DISABLE') : tAdmin('NOTIF_PUSH_DEVICE_ENABLE')}>
                              <Switch
                                size="small"
                                color="default"
                                checked={isEnabled}
                                onChange={() => handleToggleDevice(device.Device_ID, device.Enabled)}
                              />
                            </Tooltip>
                            <Tooltip title={tAdmin('NOTIF_PUSH_DEVICE_SENDTEST')}>
                              <span>
                                <IconButton size="small" sx={{ color: 'text.secondary' }} disabled={!isEnabled} onClick={() => handleTestPush(device.Device_ID)}>
                                  <NotificationsActiveIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={tAdmin('NOTIF_PUSH_DEVICE_REMOVE')}>
                              <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={() => handleDeleteDevice(device.Device_ID)}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })
                    )}
                  </>
                )}
                {hasChanges && (
                  <div className="flex justify-center px-8 pt-4">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={saving}
                      onClick={handleSave}
                    >
                      {tAdmin('SAVE')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </ABSScrollbars>
        </>
      ) : (
        <>
          {/* Notifications header */}
          <div className="flex items-center justify-between px-16 py-12 border-b">
            <Typography className="text-18 font-semibold leading-none">{t('NOTIFICATIONS')}</Typography>
            <div className="flex items-center">
              {hasTLLicence && (
                <Tooltip title={t('NOTIFICATION_SETTINGS')} placement="bottom">
                  <IconButton onClick={() => setShowSettings(true)} size="medium">
                    <ABSSvgIcon color="action">heroicons-outline:cog</ABSSvgIcon>
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('CLOSE')} placement="bottom">
                <IconButton onClick={handleClose} size="medium">
                  <ABSSvgIcon color="action">heroicons-outline:x</ABSSvgIcon>
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Action buttons */}
          {notifications.length > 0 && (
            <div className="flex items-center px-12 pt-4">
              <Tooltip title={t('MARK_ALL_READ')} placement="bottom">
                <IconButton size="small" onClick={handleMarkAllRead}>
                  <span className="flex items-center" style={{ width: 18, height: 18 }}>
                    <ABSSvgIcon size={14} color="action">heroicons-solid:check</ABSSvgIcon>
                    <ABSSvgIcon size={14} color="action" style={{ marginLeft: -8 }}>heroicons-solid:check</ABSSvgIcon>
                  </span>
                </IconButton>
              </Tooltip>
              <Tooltip title={t('DISMISS_ALL_NOTIFICATIONS')} placement="bottom">
                <IconButton size="small" onClick={handleDismissAll}>
                  <ABSSvgIcon size={18} color="action">heroicons-outline:archive</ABSSvgIcon>
                </IconButton>
              </Tooltip>
            </div>
          )}

          {notifications.length > 0 ? (
            <ABSScrollbars className="p-16">
              <div className="flex flex-col gap-12">
                {notifications.map((item, index) => (
                  <NotificationCard
                    key={item.id}
                    item={item}
                    removing={dismissingAll}
                    removeDelay={(notifications.length - 1 - index) * 50}
                    onClose={handleDismiss}
                    onAcknowledge={handleAcknowledge}
                  />
                ))}
              </div>
            </ABSScrollbars>
          ) : (
            <div className="flex flex-1 items-center justify-center p-16">
              <Typography className="text-24 text-center" color="text.secondary">
                {t('NO_NOTIFICATIONS')}
              </Typography>
            </div>
          )}
        </>
      )}
    </StyledSwipeableDrawer>
  );
}

export default withReducer('notificationPanel', reducer)(
  withReducer('userSettings', userSettingsReducer)(memo(NotificationPanel))
);
